import { useEffect, useRef, useState } from 'react';
import { PRUEF_ARTEN } from '../../../shared/akquise';
import { aktion, fmt, useDaten } from '../../daten';
import { Leer, Symbol, useHinweis, vars } from '../../komponenten/Grundbausteine';

interface Fall {
  id: number; fid: string; firma: string; art: string; seit: string; liste: { id: number; name: string; land: string; kat: string } | null;
  website: string | null; websiteTyp: string | null; branche: string | null; grund: string; versucht: string[];
  vorschlag: string | null; sicherheit: string | null; fehlt: string[]; offen: string | null; wert: string | null; bei: 'routine' | 'ki' | 'dich';
}
interface Erledigt { id: number; fid: string; firma: string; art: string; entscheidung: string; beleg: string | null; zeit: string; zurueck: boolean }
interface PruefDaten { offen: Fall[]; erledigt: { heute: number; liste: Erledigt[] }; kategorien: { name: string; ueber: string }[] }

const ohneProtokoll = (u: string) => u.replace(/^https?:\/\//, '').replace(/\/$/, '');
function seit(iso: string) {
  const d = new Date(iso), h = new Date();
  const z = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (d.toDateString() === h.toDateString()) return `heute ${z}`;
  const g = new Date(h); g.setDate(h.getDate() - 1);
  if (d.toDateString() === g.toDateString()) return 'gestern';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
}

export function Pruefen() {
  const hinweis = useHinweis();
  const { daten, neu, fehler } = useDaten<PruefDaten>('/api/akquise/pruefen');
  const [tab, setTab] = useState<'offen' | 'wartet' | 'erledigt'>('offen');
  const [art, setArt] = useState('');
  const [cur, setCur] = useState(0);
  const [felder, setFelder] = useState<Record<string, string>>({});
  const [laeuft, setLaeuft] = useState(false);
  const formular = useRef<HTMLElement>(null);

  // Für dich nur, was die Routine nicht klären konnte (im Alt-System lila); der Rest wartet auf die Routine
  const alle = (daten?.offen ?? []).filter(x => x.bei === 'dich');
  const wartend = (daten?.offen ?? []).filter(x => x.bei !== 'dich');
  const liste = alle.filter(x => !art || x.art === art);
  const pos = Math.min(cur, Math.max(0, liste.length - 1));
  const c = liste[pos];
  const P = c ? PRUEF_ARTEN[c.art] : null;

  // Felder je Fall vorbelegen (Vorschlagswerte wie im Prototyp)
  useEffect(() => {
    if (!c) return;
    const v: Record<string, string> = {};
    if (c.art === 'Kategorie und Förderung') { v.Branche = c.branche ?? ''; if (c.offen === 'Kategorie' && c.vorschlag) v.Kategorie = c.vorschlag; if (c.offen === 'Förderfähigkeit' && c.vorschlag) v['Förderfähig'] = c.vorschlag; }
    if (c.wert) { if (c.art === 'Impressum') v['Impressum-URL'] = c.wert; if (c.art === 'Impressumsdaten') v['allg. Mail'] = c.wert; }
    setFelder(v);
  }, [c?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const entscheiden = async (i: number) => {
    if (!c || !P || laeuft) return;
    setLaeuft(true);
    try {
      const r = await aktion<{ text: string }>('pruefen_entscheiden', { fall: c.id, wahl: P.optionen[i][0], felder });
      hinweis(r.text);
      neu();
    } catch (e) { hinweis(`Geht nicht: ${(e as Error).message}`); }
    setLaeuft(false);
  };

  useEffect(() => {
    if (tab !== 'offen') return;
    const taste = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || (e.target as HTMLElement).closest('input, select, textarea') || document.querySelector('#palette:not([hidden])')) return;
      if (!c || !P) return;
      const n = Number(e.key);
      if (n >= 1 && n <= P.optionen.length) { e.preventDefault(); entscheiden(n - 1); }
      if (e.key.toLowerCase() === 's') { e.preventDefault(); setCur((pos + 1) % Math.max(liste.length, 1)); }
    };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  });

  const zurueck = async (id: number) => {
    try { await aktion('pruefen_zuruecknehmen', { fall: id }); hinweis('Zurückgenommen; der Fall liegt jetzt bei dir.'); neu(); } catch (e) { hinweis(`Geht nicht: ${(e as Error).message}`); }
  };

  const feld = ([name, hilfe]: [string, string]) => {
    if (!c) return null;
    if (c.art === 'Impressumsdaten' && !c.fehlt.includes(name)) return null;
    if (c.art === 'Kategorie und Förderung' && ((c.offen === 'Kategorie' && !['Branche', 'Kategorie'].includes(name)) || (c.offen === 'Förderfähigkeit' && name !== 'Förderfähig') || (c.offen === 'Förderhöhe' && name !== 'Förderquote'))) return null;
    if (c.art === 'Kategorie und Förderung' && name === 'Förderquote' && c.offen !== 'Förderhöhe') return null;
    const id = 'pf-' + name.replace(/[^a-z]/gi, '');
    const setzen = (w: string) => setFelder(f => ({ ...f, [name]: w }));
    if (name === 'Förderfähig') return <label className="fld" key={name}><span className="lbl">{name}</span><select className="input plain" id={id} value={felder[name] ?? ''} onChange={e => setzen(e.target.value)}><option value="" /><option>ja</option><option>nein</option><option>prüfen</option></select><span className="meta">{hilfe}</span></label>;
    if (name === 'Förderquote') return <label className="fld" key={name}><span className="lbl">{name}</span><select className="input plain" id={id} value={felder[name] ?? ''} onChange={e => setzen(e.target.value)}><option value="" /><option value="80">80 %</option><option value="50">50 %</option></select><span className="meta">{hilfe}</span></label>;
    if (name === 'Kategorie') return <label className="fld" key={name}><span className="lbl">{name}</span><select className="input plain" id={id} value={felder[name] ?? ''} onChange={e => setzen(e.target.value)}><option value="" />{daten?.kategorien.map(k => <option key={k.name}>{k.name}</option>)}</select><span className="meta">{hilfe} · Überkategorie kommt von selbst</span></label>;
    return <label className="fld" key={name}><span className="lbl">{name}</span><input className="input plain" id={id} value={felder[name] ?? ''} onChange={e => setzen(e.target.value)} /><span className="meta">{hilfe}</span></label>;
  };

  return (
    <>
      <header className="page-head">
        <div><h1 id="h-pr">Prüfen</h1><p className="meta" id="prMeta">{daten ? `${fmt(alle.length)} ${alle.length === 1 ? 'Fall' : 'Fälle'} für dich · ${fmt(wartend.length)} warten auf die Routine · die Routine hat heute ${fmt(daten.erledigt.heute)} Fälle selbst entschieden` : fehler}</p></div>
        <div className="seg" id="prTabs" style={vars({ '--n': 3, '--i': tab === 'offen' ? 0 : tab === 'wartet' ? 1 : 2 })} role="group" aria-label="Bereich">
          <span className="seg-thumb" aria-hidden="true" />
          <button aria-pressed={tab === 'offen'} data-t="offen" onClick={() => setTab('offen')}>Für dich</button>
          <button aria-pressed={tab === 'wartet'} data-t="wartet" onClick={() => setTab('wartet')}>Wartet auf die Routine</button>
          <button aria-pressed={tab === 'erledigt'} data-t="erledigt" onClick={() => setTab('erledigt')}>Von der Routine erledigt</button>
        </div>
      </header>
      <div id="prBody" className="be-stack">
        {tab === 'wartet' ? (
          <section className="panel" aria-labelledby="h-wa">
            <div className="panel-head"><div><h2 id="h-wa">Wartet auf die Routine</h2><p className="meta">{fmt(wartend.length)} Fälle, die die Routine noch abarbeitet: erst öffnet die Prüf-Routine die Seiten, dann urteilt die KI-Routine nach den Regeln der Prüfung. Was beide nicht klären, kommt zu „Für dich“.</p></div></div>
            {wartend.length ? (
              <div className="t-wrap"><table className="t"><thead><tr><th>Firma</th><th>Prüfart</th><th>Stand</th><th className="t-hide-sm">Warum offen</th><th className="t-hide-sm">Seit</th></tr></thead><tbody>
                {wartend.map(x => (
                  <tr key={x.id}>
                    <td><span className="name" style={{ maxWidth: 240, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.firma}</span><span className="sub meta num">{x.fid}</span></td>
                    <td>{x.art}</td><td>{x.bei === 'routine' ? 'Prüf-Routine öffnet die Seiten' : 'bei der KI-Routine'}</td>
                    <td className="t-hide-sm meta" style={{ whiteSpace: 'normal' }}>{x.grund}</td><td className="t-hide-sm meta">{seit(x.seit)}</td>
                  </tr>
                ))}
              </tbody></table></div>
            ) : <Leer symbol="check" titel="Die Routine hat alles abgearbeitet"><p>Neue Fälle kommen mit der Anreicherung.</p></Leer>}
          </section>
        ) : tab === 'erledigt' ? (
          <section className="panel" aria-labelledby="h-erl">
            <div className="panel-head"><div><h2 id="h-erl">Von der Routine entschieden</h2><p className="meta">mit Beleg; die letzten Entscheidungen. Rückgängig legt den Fall dir vor.</p></div></div>
            {daten?.erledigt.liste.length ? (
              <div className="t-wrap"><table className="t"><thead><tr><th>Firma</th><th>Prüfart</th><th>Entscheidung</th><th className="t-hide-sm">Beleg</th><th className="t-hide-sm">Zeit</th><th /></tr></thead><tbody>
                {daten.erledigt.liste.map(e => (
                  <tr key={e.id}>
                    <td><span className="name" style={{ maxWidth: 240, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.firma}</span><span className="sub meta num">{e.fid}</span></td>
                    <td>{e.art}</td><td>{e.zurueck ? <span className="meta">zurückgenommen</span> : e.entscheidung}</td>
                    <td className="t-hide-sm meta" style={{ whiteSpace: 'normal' }}>{e.beleg}</td><td className="t-hide-sm meta">{seit(e.zeit)}</td>
                    <td>{!e.zurueck && <button className="btn btn-sm btn-ghost" onClick={() => zurueck(e.id)}>Rückgängig</button>}</td>
                  </tr>
                ))}
              </tbody></table></div>
            ) : <Leer symbol="check" titel="Noch keine Entscheidungen der Routine"><p>Hier stehen die Fälle, die die Routine selbst entschieden hat, mit Beleg.</p></Leer>}
          </section>
        ) : <>
          <div className="chips">
            <button className="chip filt" aria-pressed={!art} onClick={() => { setArt(''); setCur(0); }}>Alle {alle.length}</button>
            {Object.keys(PRUEF_ARTEN).map(a => { const n = alle.filter(x => x.art === a).length; return <button key={a} className="chip filt" aria-pressed={art === a} disabled={!n} onClick={() => { setArt(a); setCur(0); }}>{a} {n}</button>; })}
          </div>
          {!daten ? null : !c || !P ? (
            <div className="panel"><Leer symbol="check" titel="Nichts mehr für dich"><p>Alle Restfälle{art ? ` zu „${art}“` : ''} sind entschieden.{wartend.length ? ` ${fmt(wartend.length)} Fälle arbeitet die Routine noch ab.` : ''}</p><button className="btn" onClick={() => setTab('erledigt')}>Entscheidungen der Routine ansehen</button></Leer></div>
          ) : (
            <div className="pr-grid">
              <div className="panel pr-list" role="listbox" aria-label="Fälle">
                {liste.map((x, i) => (
                  <button key={x.id} className="pr-item" role="option" aria-selected={i === pos} onClick={() => setCur(i)}>
                    <span className="pr-top"><strong>{x.firma}</strong><span className="meta">{seit(x.seit)}</span></span>
                    <span className="pr-sub"><span className={`state ${PRUEF_ARTEN[x.art].bremst ? 'warn' : 'neutral'}`}>{x.art}</span><span className="meta">{x.liste ? `${x.liste.name} · ${x.liste.land}` : ''}</span></span>
                  </button>
                ))}
              </div>
              <section className="panel pr-case" aria-labelledby="prQ" ref={formular}>
                <div className="panel-head"><div>
                  <span className="meta num">{c.fid}{c.liste ? ` · ${c.liste.land} › ${c.liste.kat} › ${c.liste.name}` : ''} · Fall {pos + 1} von {liste.length}</span>
                  <h2 id="prQ">{P.frage}</h2>
                  <span className="meta">{c.firma}{c.website && <> · <a href={c.website} target="_blank" rel="noopener noreferrer">{ohneProtokoll(c.website)}</a></>}</span>
                </div><span className={`state ${P.bremst ? 'warn' : 'neutral'}`}>{P.bremst ? 'hält die Ansprache an' : 'bremst nicht'}</span></div>
                <div className="callout"><Symbol id="alert" /><div><strong>Warum das bei dir landet</strong><span>{c.grund}</span>
                  {c.versucht.length > 0 && <ul className="tried">{c.versucht.map((v, i) => <li key={i}>{v}</li>)}</ul>}
                  {c.vorschlag ? <span className="meta">Vorschlag der Routine ({c.sicherheit}): <strong style={{ fontWeight: 600 }}>{c.vorschlag}</strong></span> : <span className="meta">Die Routine hat keinen Vorschlag.</span>}
                </div></div>
                {c.art === 'Website-Typ' ? <dl className="facts"><dt>Website-Typ heute</dt><dd>{c.websiteTyp ?? 'offen'}</dd></dl> : c.fehlt.length > 0 ? <dl className="facts"><dt>Es fehlt</dt><dd>{c.fehlt.join(', ')}</dd></dl> : null}
                <div className="fld-row">{P.felder.map(feld)}</div>
                <div className="pr-actions">
                  {P.optionen.map(([o], i) => <button key={o} className={o === c.vorschlag || c.art === 'Kategorie und Förderung' ? 'btn btn-primary' : 'btn'} disabled={laeuft} onClick={() => entscheiden(i)}><kbd>{i + 1}</kbd>{o}</button>)}
                  <button className="btn btn-ghost" onClick={() => setCur((pos + 1) % Math.max(liste.length, 1))}><kbd>S</kbd>Überspringen</button>
                </div>
                <p className="hint">Tasten 1 bis {P.optionen.length} entscheiden, S überspringt; danach kommt der nächste Fall.</p>
              </section>
            </div>
          )}
        </>}
      </div>
    </>
  );
}
